import DescriptionIcon from '@mui/icons-material/Description'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import { Avatar, Box, CardMedia, Container, Divider, Stack, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import * as React from 'react'

import { userReadonlyAtom, usersByIdsAtomFamily } from '../../domains/user/AtomUsers'
import { avatarUrl } from '../../utils/gravatar'
import { VIEW_TYPES } from '../../utils/utility'
import InfoSection from '../common/InfoSection'
import UserContactDetail from '../common/UserContactDetail'

const IMAGES = [
  'https://images.pexels.com/photos/848612/pexels-photo-848612.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/257961/pexels-photo-257961.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/358046/pexels-photo-358046.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/1271147/pexels-photo-1271147.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/376697/pexels-photo-376697.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/2433363/pexels-photo-2433363.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/1973293/pexels-photo-1973293.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/2698915/pexels-photo-2698915.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/2422265/pexels-photo-2422265.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
]

function getNumberFromString(str, n) {
  // Hash the string into a number
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0 // Convert to 32-bit integer
  }

  // Map the hash to the range [0, n]
  return Math.abs(hash) % (n + 1)
}

const image = (id) => {
  return IMAGES[getNumberFromString(id, IMAGES.length)]
}

/**
 * PrivateSessionDetail component that displays detailed information about a private session
 * @param {Object} props
 * @param {Object} props.booking - The booking object containing session details
 * @returns {JSX.Element} Rendered component with session details
 */
export default function PrivateSessionDetail({ booking, viewer = 'all' }) {
  const instructor = useAtomValue(userReadonlyAtom(booking.instructorId))
  const students = useAtomValue(usersByIdsAtomFamily(booking.participantIds))

  return (
    <>
      <Container sx={{ m: 0, p: 0 }}>
        <CardMedia component="img" height="250" image={image(booking.resourceId)} alt={booking.resourceId} />
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Instructor Information */}
          {instructor && (viewer === 'all' || viewer === VIEW_TYPES.USER) && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                Instructor Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={avatarUrl(instructor)} sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                  {instructor.name?.[0]}
                </Avatar>
                <Typography variant="h6">{instructor.name}</Typography>
                <UserContactDetail user={instructor} />
              </Box>
              <Divider />
            </Box>
          )}

          {/* Student Information */}
          {students.length > 0 && (viewer === 'all' || viewer === VIEW_TYPES.INSTRUCTOR) && (
            <>
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                  Student Information
                </Typography>
                {students.map((student, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: index < students.length - 1 ? 2 : 0 }}>
                    <Avatar src={avatarUrl(student)} sx={{ width: 56, height: 56, bgcolor: 'secondary.main' }}>
                      {student.name?.[0]}
                    </Avatar>
                    <Typography variant="h6">{student.name}</Typography>
                    <UserContactDetail user={student} />
                  </Box>
                ))}
              </Box>
              <Divider />
            </>
          )}

          {/* Session Information */}
          <InfoSection
            title="Session Information"
            info={{
              date: booking.date,
              timeSlot: booking.timeSlot,
              location: booking.location || booking.skiResort,
              sport: booking.sportType || booking.sport,
              price: booking.price,
              cancellationDeadline: booking.cancellationDeadline,
              registrationDeadline: booking.registrationDeadline,
              status: booking.status,
            }}
          />

          <Divider />

          {/* Private Session Description */}
          {booking.description && (
            <>
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="action" />
                  Private Session Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', pl: 4 }}>
                  {booking.description}
                </Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Student Notes */}
          {booking.notes && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                <StickyNote2Icon color="action" />
                Student Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', pl: 4 }}>
                {booking.notes}
              </Typography>
            </Box>
          )}
        </Stack>
      </Container>
    </>
  )
}
